<template>
  <div class="container-fluid search-page rfo-page rfo-page--standard oa-workspace">
    <div class="mb-3">
      <router-link :to="{ name: 'pay-operations-order-amendments' }" class="oa-back-link">
        <i class="fa fa-arrow-left me-1"></i>Back to Queue
      </router-link>
    </div>

    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-2">Loading case details...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger outline" role="alert">
      <i class="fa fa-exclamation-triangle me-2"></i>{{ error }}
      <div class="mt-2">
        <button class="btn btn-sm btn-outline-danger" @click="fetchCase">Retry</button>
      </div>
    </div>

    <template v-else-if="caseDoc">
      <!-- Header -->
      <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
        <div class="card-header bg-primary text-white">
          <h5 class="mb-0">
            <i class="fa fa-file-text-o me-2"></i>
            Order {{ shortUuid(caseDoc.orderUuid) }}
          </h5>
        </div>
        <div class="card-body border border-primary rounded-3">
          <div class="oa-card-toolbar">
            <span class="badge" :class="stateBadgeClass(caseDoc.orderState)">{{
              caseDoc.orderState
            }}</span>
            <button
              class="btn btn-sm btn-outline-primary"
              :disabled="isRefreshing || !hasAgentAccess"
              @click="refreshCase"
            >
              <span v-if="isRefreshing">
                <span class="spinner-border spinner-border-sm me-1"></span>Refreshing...
              </span>
              <span v-else><i class="fa fa-refresh me-1"></i>Refresh</span>
            </button>
          </div>
          <div class="row">
            <div class="col-md-6">
              <dl class="row mb-0 small">
                <dt class="col-sm-4">Order UUID</dt>
                <dd class="col-sm-8">
                  <span class="font-monospace">{{ caseDoc.orderUuid }}</span>
                  <button class="btn btn-link btn-sm p-0 ms-1" @click="copyUuid(caseDoc.orderUuid)">
                    <i class="fa fa-copy"></i>
                  </button>
                </dd>
                <dt class="col-sm-4">Partner</dt>
                <dd class="col-sm-8">{{ caseDoc.partnerName || '—' }}</dd>
                <dt class="col-sm-4">POI</dt>
                <dd class="col-sm-8">{{ caseDoc.poiId || '—' }}</dd>
                <dt class="col-sm-4">Order Type</dt>
                <dd class="col-sm-8">{{ caseDoc.orderType || '—' }}</dd>
              </dl>
            </div>
            <div class="col-md-6">
              <dl class="row mb-0 small">
                <dt class="col-sm-4">Amount</dt>
                <dd class="col-sm-8">
                  {{ formatCurrency(caseDoc.totalAmount, caseDoc.currency) }}
                </dd>
                <dt class="col-sm-4">Case Status</dt>
                <dd class="col-sm-8">{{ caseStatusLabel(caseDoc.status) }}</dd>
                <dt class="col-sm-4">Assignee</dt>
                <dd class="col-sm-8">
                  {{ caseDoc.assigneeDisplayName || caseDoc.assignee || 'Unassigned' }}
                </dd>
                <dt class="col-sm-4">Days Open</dt>
                <dd class="col-sm-8">
                  <span :class="slaColorClass(caseDoc.daysOpen)">{{
                    caseDoc.daysOpen ?? '—'
                  }}</span>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Multi-incident warning (FQ-5) -->
      <div v-if="incidents.length > 1" class="alert alert-warning outline small mb-3">
        <i class="fa fa-exclamation-triangle me-1"></i>
        This order belongs to <strong>{{ incidents.length }}</strong> incidents:
        <span v-for="(inc, i) in incidents" :key="inc.id">
          {{ inc.name }}<span v-if="i < incidents.length - 1">, </span>
        </span>
      </div>

      <!-- Classification -->
      <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
        <div class="card-header bg-secondary text-white">
          <h6 class="mb-0">
            <span>
              <i class="fa me-1" :class="classificationDisplay(caseDoc.classification).icon"></i>
              Classification
            </span>
            <span
              class="badge ms-2"
              :class="classificationDisplay(caseDoc.classification).badgeClass"
            >
              {{ classificationDisplay(caseDoc.classification).label }}
            </span>
          </h6>
        </div>
        <div class="card-body">
          <div v-if="caseDoc.classificationEvidence" class="small">
            <dl class="row mb-0">
              <template v-for="(value, key) in caseDoc.classificationEvidence" :key="key">
                <dt class="col-sm-4">{{ formatEvidenceKey(key) }}</dt>
                <dd class="col-sm-8">{{ value ?? '—' }}</dd>
              </template>
            </dl>
          </div>
          <p v-else class="text-muted small mb-0">No classification evidence available.</p>

          <template v-if="caseDoc.classification === 'overshoot' && order?.transaction">
            <hr />
            <h6 class="small fw-bold">
              <i class="fa fa-balance-scale me-1"></i>Pre-auth vs Captured Amount
            </h6>
            <div class="row small">
              <div class="col-md-6">
                <div class="border rounded p-2 mb-2">
                  <strong>Pre-authorized:</strong>
                  <span class="ms-1">{{
                    formatCurrency(order.transaction.authorization?.amount, caseDoc.currency)
                  }}</span>
                </div>
              </div>
              <div class="col-md-6">
                <div class="border rounded p-2 mb-2">
                  <strong>Captured:</strong>
                  <span class="ms-1">{{
                    formatCurrency(order.transaction.capture?.amount, caseDoc.currency)
                  }}</span>
                </div>
              </div>
            </div>
            <div class="alert alert-info outline small mb-0">
              <i class="fa fa-info-circle me-1"></i>
              Overshoot: pre-auth and captured amounts differ. The default action is
              <strong>completeManually</strong>.
            </div>
          </template>
        </div>
      </div>

      <ProductEditor
        v-if="order"
        :products="order?.products || []"
        :editable="hasAgentAccess"
        :default-currency="caseDoc.currency || 'EUR'"
        @update:products="editedProducts = $event"
      />

      <ActionPanel
        v-if="hasAgentAccess"
        :allowed-actions="allowedActions"
        :warnings="caseDoc.warnings || []"
        :show-capture-toggle="true"
        :initial-capture-on-amend="captureOnAmend"
        @action="onActionSelected"
        @update:captureOnAmend="captureOnAmend = $event"
      />

      <div
        v-if="hasAgentAccess && caseDoc.classification === 'drive_away'"
        class="alert alert-info outline d-flex flex-wrap align-items-center gap-2"
      >
        <i class="fa fa-car me-1"></i>
        <span class="flex-grow-1">
          This case is classified as a drive-away. The guided flow completes the order and creates
          the invoice with an operator-chosen payment object in one place.
        </span>
        <router-link
          :to="{ name: 'pay-operations-drive-away', params: { uuid: caseDoc.orderUuid } }"
          class="btn btn-sm btn-primary text-white"
        >
          Open Drive-Away Flow
        </router-link>
      </div>

      <!-- Timeline -->
      <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
        <div class="card-header bg-secondary text-white">
          <h6 class="mb-0"><i class="fa fa-history me-2"></i>Amendment Timeline</h6>
        </div>
        <div class="card-body">
          <div v-if="attempts.length > 0">
            <div
              v-for="attempt in attempts"
              :key="attempt.attemptId"
              class="border-start border-3 ps-3 mb-3"
              :class="attemptBorderClass(attempt.result)"
            >
              <div class="d-flex justify-content-between">
                <strong class="small">{{ attempt.action }}</strong>
                <ZonedDateTime
                  class="text-muted small"
                  :value="attempt.createdAt"
                  :zone="orderZone.zone"
                  :kind="orderZone.kind"
                  caption
                />
              </div>
              <div class="small text-muted">
                by {{ attempt.actorDisplayName || attempt.actor || 'system' }} ·
                {{ attempt.result }}
              </div>
              <div v-if="attempt.reason" class="small mt-1">Reason: {{ attempt.reason }}</div>
              <div v-if="attempt.errorDetails" class="small text-danger mt-1">
                {{ attempt.errorDetails }}
              </div>
            </div>
          </div>
          <p v-else class="text-muted small mb-0">No amendment attempts recorded yet.</p>
        </div>
      </div>

      <RefundTrackingPanel
        :order-uuid="orderUuid"
        :can-edit="canEditRefund"
        :timezone="orderZone.zone"
        :timezone-kind="orderZone.kind"
      />

      <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
        <div class="card-body text-center">
          <router-link
            :to="{ name: 'find-data-by-order-uuid', query: { uuid: caseDoc.orderUuid } }"
            class="oa-back-link"
          >
            <i class="fa fa-external-link me-1"></i>
            Open in Customer Support — Find by Order UUID
          </router-link>
        </div>
      </div>

      <PayOpsUsageInstructions
        class="mt-4"
        description="Review a single case, edit the draft, preview the change, and execute the right action."
        :required-roles="detailRequiredRoles"
        :workflow-notes="detailWorkflowNotes"
      />
    </template>

    <div v-else class="oa-empty-state">
      <i class="fa fa-search fa-2x"></i>
      <p class="mb-2">No case data could be loaded for this order.</p>
      <button class="btn btn-sm btn-outline-primary" @click="fetchCase">
        <i class="fa fa-refresh me-1"></i>Try Again
      </button>
    </div>

    <PreviewConfirmModal
      :visible="previewModalOpen"
      :order-uuid="orderUuid"
      :action="selectedAction"
      :edited-products="editedProducts"
      :edited-billing="editedBilling"
      :capture-on-amend="captureOnAmend"
      @close="previewModalOpen = false"
      @executed="onExecuted"
    />

    <RefundCancelConfirmModal
      :visible="refundCancelOpen"
      :order-uuid="orderUuid"
      :is-submitting="isSubmitting"
      :has-gift-cards="hasGiftCards"
      @close="refundCancelOpen = false"
      @submit="onRefundCancelSubmit"
    />

    <ForceCompleteConfirmModal
      :visible="forceCompleteOpen"
      :is-submitting="isSubmitting"
      @close="forceCompleteOpen = false"
      @submit="onForceCompleteSubmit"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import ProductEditor from './components/ProductEditor.vue';
import { buildProductAmendments } from '@/services/ryd-pay-operations/order-amendments/product-amendments';
import ActionPanel from './components/ActionPanel.vue';
import PreviewConfirmModal from './components/PreviewConfirmModal.vue';
import RefundTrackingPanel from './components/RefundTrackingPanel.vue';
import RefundCancelConfirmModal from './components/RefundCancelConfirmModal.vue';
import ForceCompleteConfirmModal from './components/ForceCompleteConfirmModal.vue';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import {
  copyToClipboard,
  formatCurrency,
  shortIdentifier as shortUuid,
} from '@/utils/display-formatters';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveOrderZone, resolveUserTimezone } from '@/utils/timezoneDisplay';
import {
  slaColorClass,
  stateBadgeClass,
  classificationDisplay,
  caseStatusLabel,
} from '@/services/ryd-pay-operations/order-amendments/formatters';
import { useAmendmentRoles } from '@/services/ryd-pay-operations/order-amendments/use-amendment-roles';
import {
  runPreview,
  runExecute,
} from '@/services/ryd-pay-operations/order-amendments/amendment-api';
import PayOpsUsageInstructions from '../shared/PayOpsUsageInstructions.vue';
import {
  PAY_OPS_DETAIL_ROLES,
  buildDetailWorkflowNotes,
} from '@/services/ryd-pay-operations/order-amendments/usage-instructions-config';

const props = defineProps({
  uuid: { type: String, required: true },
});

const route = useRoute();
const toast = useToast();

const { hasAgentAccess, hasAdminAccess, canEditRefund } = useAmendmentRoles();

const detailRequiredRoles = PAY_OPS_DETAIL_ROLES;
const detailWorkflowNotes = buildDetailWorkflowNotes();

const caseDoc = ref(null);
const order = ref(null);
const allowedActions = ref([]);
const attempts = ref([]);
const incidents = ref([]);
const isLoading = ref(false);
const isRefreshing = ref(false);
const error = ref('');

const editedProducts = ref([]);
const editedBilling = ref(null);
const captureOnAmend = ref(false);

const previewModalOpen = ref(false);
const refundCancelOpen = ref(false);
const forceCompleteOpen = ref(false);
const selectedAction = ref('');
const isSubmitting = ref(false);

const orderUuid = computed(() => props.uuid || route.params.uuid);
const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);
const orderZone = computed(() => resolveOrderZone(order.value, userTimezone));

const hasGiftCards = computed(() =>
  (order.value?.products ?? []).some((p) => p?.productType === 'GIFT_CARD'),
);

async function fetchCase() {
  isLoading.value = true;
  error.value = '';
  try {
    const [caseRes, attemptsRes, incidentsRes] = await Promise.allSettled([
      apiClient.rpIntegration.get(
        API_PATHS.rpIntegration.payOperations.orderAmendments.caseByUuid(orderUuid.value),
      ),
      apiClient.rpIntegration.get(
        API_PATHS.rpIntegration.payOperations.orderAmendments.attempts(orderUuid.value),
      ),
      apiClient.rpIntegration.get(
        API_PATHS.rpIntegration.payOperations.orderAmendments.caseIncidents(orderUuid.value),
      ),
    ]);

    if (caseRes.status === 'fulfilled') {
      const data = caseRes.value.data;
      caseDoc.value = data?.case ?? data;
      order.value = data?.order ?? null;
      allowedActions.value = data?.allowedActions ?? [];
    } else {
      error.value =
        caseRes.reason?.response?.data?.message ??
        caseRes.reason?.message ??
        'Failed to load case.';
    }
    if (attemptsRes.status === 'fulfilled') {
      attempts.value = attemptsRes.value.data?.attempts ?? [];
    }
    if (incidentsRes.status === 'fulfilled') {
      incidents.value = incidentsRes.value.data?.items ?? [];
    }
  } catch (err) {
    error.value = err?.response?.data?.message ?? err?.message ?? 'Failed to load case.';
  } finally {
    isLoading.value = false;
  }
}

async function refreshCase() {
  isRefreshing.value = true;
  try {
    await apiClient.rpIntegration.post(
      API_PATHS.rpIntegration.payOperations.orderAmendments.refresh(orderUuid.value),
    );
    toast.success('Case refreshed.');
    await fetchCase();
  } catch (err) {
    toast.error(err?.response?.data?.message ?? 'Refresh failed.');
  } finally {
    isRefreshing.value = false;
  }
}

async function copyUuid(uuid) {
  const ok = await copyToClipboard(uuid);
  if (ok) toast.success('UUID copied!');
}

function formatEvidenceKey(key) {
  return String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function attemptBorderClass(result) {
  switch (result) {
    case 'success':
      return 'border-success';
    case 'partial':
      return 'border-warning';
    case 'failure':
      return 'border-danger';
    default:
      return 'border-secondary';
  }
}

function onActionSelected(action) {
  selectedAction.value = action;
  if (action === 'refundAndCancelManually') {
    refundCancelOpen.value = true;
    return;
  }
  if (action === 'forceCompleteManually') {
    forceCompleteOpen.value = true;
    return;
  }
  previewModalOpen.value = true;
}

/**
 * Builds the OrderAmendmentsDto-shaped draft the backend expects. Returns null, after a toast,
 * when a product row is incomplete: order-service would reject the whole amendment anyway.
 */
function buildDraft(extra = {}) {
  const { errors, ...products } = buildProductAmendments(editedProducts.value);
  if (errors.length) {
    toast.error(`Fix the product rows first: ${errors.join('; ')}`);
    return null;
  }
  const draft = { ...products, captureOnAmend: captureOnAmend.value, ...extra };
  if (editedBilling.value) draft.billingDetails = editedBilling.value;
  return draft;
}

async function onRefundCancelSubmit(data) {
  const draft = buildDraft({ isRefundEligible: true });
  if (!draft) return;
  isSubmitting.value = true;
  try {
    const preview = await runPreview(orderUuid.value, {
      action: 'refundAndCancelManually',
      draft,
      reason: data.reason,
      reasonCategory: data.reasonCategory,
    });
    if (preview.blockers?.length) {
      toast.error(`Cannot proceed: ${preview.blockers.join('; ')}`);
      return;
    }
    await runExecute(orderUuid.value, {
      action: 'refundAndCancelManually',
      previewToken: preview.previewToken,
      reasonCategory: data.reasonCategory,
      confirmations: { uuidLast8: orderUuid.value.slice(-8) },
    });
    toast.success('Refund & Cancel executed. Refund tracker created (manual PSP action).');
    refundCancelOpen.value = false;
    await fetchCase();
  } catch (err) {
    toast.error(err?.response?.data?.message ?? 'Refund & Cancel failed.');
  } finally {
    isSubmitting.value = false;
  }
}

async function onForceCompleteSubmit(data) {
  const draft = buildDraft();
  if (!draft) return;
  isSubmitting.value = true;
  try {
    const preview = await runPreview(orderUuid.value, {
      action: 'forceCompleteManually',
      draft,
      reason: data.reason,
      reasonCategory: data.reasonCategory,
    });
    if (preview.blockers?.length) {
      toast.error(`Cannot proceed: ${preview.blockers.join('; ')}`);
      return;
    }
    await runExecute(orderUuid.value, {
      action: 'forceCompleteManually',
      previewToken: preview.previewToken,
      reasonCategory: data.reasonCategory,
      confirmations: { forceCompleteAcknowledged: true },
    });
    toast.success('Force Complete executed.');
    forceCompleteOpen.value = false;
    await fetchCase();
  } catch (err) {
    toast.error(err?.response?.data?.message ?? 'Force Complete failed.');
  } finally {
    isSubmitting.value = false;
  }
}

async function onExecuted(result) {
  if (result?.success || result?.partial) {
    toast.success('Amendment executed.');
    previewModalOpen.value = false;
    await fetchCase();
  }
}

watch(orderUuid, fetchCase);
onMounted(fetchCase);

// expose to template (not strictly needed with <script setup>, but used by v-if guards)
defineExpose({ hasAdminAccess });
</script>
